import { supabase } from '@/lib/supabase';
import { Class } from '@/types/classTypes';
import { handleError, ErrorSeverity, asyncErrorHandler } from '@/utils/errorHandler';

/**
 * Service responsible for retrieving classes and class data
 */
export const ClassQueryService = {
  /**
   * Fetches all classes for an organization
   */
  async getClasses(organizationId: string): Promise<Class[]> {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform the database results to match the Class interface
      return (data || []).map(item => ({
        ...item,
        description: item.description || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
        is_event: item.is_event !== undefined ? item.is_event : false,
      })) as Class[];
    } catch (error) {
      handleError(
        error,
        'Error fetching classes',
        ErrorSeverity.WARNING,
        { additionalInfo: { organizationId } }
      );
      return [];
    }
  },

  /**
   * Fetches a single class by ID with its schedules
   */
  async getClassById(classId: string): Promise<Class> {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          schedules:class_schedules!class_schedules_class_id_fkey(*)
        `)
        .eq('id', classId)
        .single();
      
      if (error) throw error;
      
      // Transform the database result to match the Class interface
      const transformedData: Class = {
        ...data,
        description: data.description || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
        is_event: data.is_event !== undefined ? data.is_event : false,
      };
      
      // Debug logging for payment fields
      console.log('ClassQueryService.getClassById debug:', {
        classId,
        price_cents: data.price_cents,
        payment_policy: data.payment_policy,
        payment_deadline_days: data.payment_deadline_days,
        transformedData_price_cents: transformedData.price_cents
      });
      
      return transformedData;
    } catch (error) {
      handleError(
        error,
        'Error fetching class details',
        ErrorSeverity.ERROR,
        { additionalInfo: { classId } }
      );
      throw error;
    }
  },
  
  /**
   * Get students who are not enrolled in a specific class
   */
  async getStudentsNotEnrolledInClass(classId: string, organizationId?: string) {
    try {
      // First get all enrolled student IDs for this class
      const { data: enrollments, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select('student_id')
        .eq('class_id', classId);
      
      if (enrollmentsError) throw enrollmentsError;
      
      // If we have enrollments, create an array of enrolled student IDs to exclude
      const enrolledStudentIds = enrollments?.map(e => e.student_id).filter(Boolean) || [];
      
      // Build the query for students
      let query = supabase
        .from('students')
        .select('*');
      
      // If we have enrolled students, exclude them from the results
      if (enrolledStudentIds.length > 0) {
        query = query.not('id', 'in', `(${enrolledStudentIds.join(',')})`);
      }
      
      // Execute the query
      const { data: students, error: studentsError } = await query;
        
      if (studentsError) throw studentsError;
      console.log("Available students for enrollment:", students?.length || 0);
      return students || [];
    } catch (error) {
      console.error("Error fetching non-enrolled students:", error);
      handleError(
        error,
        'Error fetching non-enrolled students',
        ErrorSeverity.ERROR,
        { additionalInfo: { classId } }
      );
      return [];
    }
  }
};
